import { useState } from 'react';
import { X, PartyPopper, ArrowLeftRight, Image, MapPin, DollarSign, Calendar, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { UserProfile, FeedType } from '@/types';
import { feedApi, CreateFeedPostPayload } from '@/lib/api';
import { toast } from 'sonner';

interface PostModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserProfile;
  onSubmit?: (data: CreateFeedPostPayload) => Promise<void>;
}

export const PostModal = ({ isOpen, onClose, user, onSubmit }: PostModalProps) => {
  const [postType, setPostType] = useState<FeedType>('party');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [price, setPrice] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload: CreateFeedPostPayload = {
        type: postType,
        title: title.trim(),
        description: description.trim(),
        location: location.trim() || undefined,
        date: date || undefined,
        time: time || undefined,
        price: price ? parseFloat(price) : undefined,
      };

      // Use callback if provided, otherwise use API directly
      if (onSubmit) {
        await onSubmit(payload);
      } else {
        await feedApi.createPost(payload);
        toast.success('Post created successfully!');
      }
      
      // Reset form and close
      setTitle('');
      setDescription('');
      setLocation('');
      setDate('');
      setTime('');
      setPrice('');
      setPostType('party');
      onClose();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to create post');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form on close
    setTitle('');
    setDescription('');
    setLocation('');
    setDate('');
    setTime('');
    setPrice('');
    setPostType('party');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal Content */}
      <div className="relative w-full max-w-md mx-4 bg-background rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold">Create Post</h2>
          <button 
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-muted transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User Info */}
        <div className="flex items-center gap-3 p-4 border-b">
          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
            <span className="text-sm font-bold text-primary">
              {user.name?.charAt(0) || 'U'}
            </span>
          </div>
          <div>
            <p className="font-medium">{user.name}</p>
            <p className="text-xs text-muted-foreground capitalize">{user.role}</p>
          </div>
        </div>

        {/* Post Type Selection */}
        <div className="p-4 space-y-3">
          <label className="text-sm font-medium">Post Type</label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setPostType('party')}
              className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                postType === 'party' 
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/30' 
                  : 'border-border hover:border-purple-300'
              }`}
            >
              <PartyPopper className={`w-5 h-5 ${postType === 'party' ? 'text-purple-500' : 'text-muted-foreground'}`} />
              <span className={`font-medium ${postType === 'party' ? 'text-purple-600 dark:text-purple-400' : ''}`}>
                Party 🎉
              </span>
            </button>
            <button
              type="button"
              onClick={() => setPostType('trade')}
              className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                postType === 'trade' 
                  ? 'border-green-500 bg-green-50 dark:bg-green-950/30' 
                  : 'border-border hover:border-green-300'
              }`}
            >
              <ArrowLeftRight className={`w-5 h-5 ${postType === 'trade' ? 'text-green-500' : 'text-muted-foreground'}`} />
              <span className={`font-medium ${postType === 'trade' ? 'text-green-600 dark:text-green-400' : ''}`}>
                Trade 🔄
              </span>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Title *</label>
            <Input
              placeholder={postType === 'party' ? "e.g., Weekend Party at Frat House" : "e.g., Selling Textbooks"}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Description *</label>
            <Textarea
              placeholder={postType === 'party' 
                ? "Tell people about your event..." 
                : "Describe what you're trading or selling..."
              }
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              required
            />
          </div>

          {/* Location */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              Location
            </label>
            <Input
              placeholder="e.g., Student Union Building"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                Date
              </label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                Time
              </label>
              <Input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </div>
          </div>

          {/* Price (for trade) */}
          {postType === 'trade' && (
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-muted-foreground" />
                Price (optional)
              </label>
              <Input
                type="number"
                placeholder="0.00"
                min="0"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-12 rounded-xl gradient-warm"
          >
            {isSubmitting ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Posting...
              </>
            ) : (
              <>
                {postType === 'party' ? '🎉' : '🔄'} Create Post
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PostModal;

